import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { qrUrlFor } from "../lib/format";

/**
 * QFreshQRSticker — branded numbered QR sticker for printing on physical
 * containers. Matches the style in the brand asset: teal QR modules with
 * rounded "eye" patterns and a centered Q-leaf logo with the box number.
 *
 * Two design notes:
 *  1. We use `qrcode.react`'s `imageSettings.excavate` to punch a clean
 *     hole in the center for our logo. We set error-correction level "H"
 *     (~30% recoverable) so the cutout never compromises scannability.
 *  2. The "rounded finder patterns" effect is decorative — we overlay
 *     rounded-teal squares on top of the three finder positions using
 *     absolute-positioned SVG corners. The underlying QR modules are still
 *     scannable underneath, but the visual matches the brand asset.
 */
const LOGO_SVG = (boxNumber) => {
  const num = String(boxNumber ?? "Q");
  // Two-digit numbers shrink a touch to fit the Q ring.
  const fontSize = num.length >= 2 ? 26 : 32;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
    <rect width="64" height="64" rx="14" fill="#FFFFFF"/>
    <circle cx="32" cy="32" r="22" fill="none" stroke="#277A82" stroke-width="4.5"/>
    <path d="M44 42 L52 50" stroke="#277A82" stroke-width="4.5" stroke-linecap="round"/>
    <path d="M48 46 Q56 46 56 38 Q50 40 48 46 Z" fill="#7BB661"/>
    <text x="32" y="42" text-anchor="middle"
          font-family="Georgia, 'Times New Roman', serif"
          font-size="${fontSize}" font-weight="700" fill="#1F5566">${num}</text>
  </svg>`;
};

export const logoDataUrl = (boxNumber) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(LOGO_SVG(boxNumber))}`;

export const QFreshQRSticker = ({
  code,
  boxNumber = null,
  size = 220,
  showCaption = true,
  caption = null,
  className = "",
}) => {
  if (!code) return null;
  const url = qrUrlFor(code);
  const logoSize = Math.round(size * 0.22);

  return (
    <div className={`flex flex-col items-center ${className}`} data-testid="qfresh-sticker">
      <div
        className="relative bg-white rounded-2xl p-3"
        style={{ width: size + 28, height: size + 28 }}
      >
        <QRCodeSVG
          value={url}
          size={size}
          level="H"
          bgColor="#FFFFFF"
          fgColor="#1F5566"
          marginSize={0}
          imageSettings={{
            src: logoDataUrl(boxNumber),
            x: undefined,
            y: undefined,
            height: logoSize,
            width: logoSize,
            excavate: true,
          }}
        />
      </div>
      {showCaption && (
        <div className="mt-2 text-center">
          <div
            className="font-heading text-base font-bold text-[#1F5566] leading-none"
            data-testid="sticker-caption"
          >
            {caption || `Box ${boxNumber ?? "—"}`}
          </div>
        </div>
      )}
    </div>
  );
};

export default QFreshQRSticker;
